import { ComponentProps, useMemo, useState } from 'react';
import { SectionSelector } from 'src/components/Selectors/SectionRowSelector';
import { Content } from 'src/contexts/ContentContext';
import {
  EventMapContextProvider,
  useEventMapContext,
} from 'src/contexts/EventMapContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { isSectionTypeGeneralAdmission } from 'src/utils/ticketUtils';
import {
  Event,
  MergedListingSectionTypeCandidate,
  MergedSectionTypeResolution,
  VenueMapSectionType,
} from 'src/WebApiController';

import * as styles from './MergeSectionTypePrompt.css';

// POS-7295: the only distinction that matters to the seller is whether a source is general admission
// or not — the underlying section type values (e.g. GeneralAdmissionWithQueueNumber) are not meaningful
// to them, so sources that would otherwise look identical are labelled with this instead.
const GENERAL_ADMISSION_LABEL = 'General Admission';
const NON_GENERAL_ADMISSION_LABEL = 'Reserved Seating';

const NO_SELECTION = '-1';

export type MergeSectionTypePromptProps = {
  resolution: MergedSectionTypeResolution;
  candidates?: MergedListingSectionTypeCandidate[] | null;
  selectedSectionTypeId?: VenueMapSectionType | null;
  onSelect: (sectionType: VenueMapSectionType) => void;
};

/**
 * POS-7295: Lets the seller disambiguate the merged listing's section type.
 *
 * - RequiresSourceSelection: the source listings disagree (general admission vs non-general admission),
 *   so the seller pick which source listing's section type the merged listing should use.
 * - RequiresSectionMapping: no source listing has a section type, so the seller maps a venue-config section.
 *
 * Uses the same searchable section picker used elsewhere in POS (SectionSelector / PosSelect) rather than a
 * bespoke control.
 */
export const MergeSectionTypePrompt = ({
  resolution,
  candidates,
  selectedSectionTypeId,
  onSelect,
}: MergeSectionTypePromptProps) => {
  const { venueMapInfo } = useEventMapContext();
  const sections = venueMapInfo?.sections ?? [];

  // Tracks the raw selection (source listing id or venue section id) for immediate display feedback.
  const [selectedValue, setSelectedValue] = useState<string | undefined>();

  const candidateOptions = useMemo(() => {
    const options = (candidates ?? []).map((candidate) => {
      // Include the seat range so sources that share a section and row (common for general
      // admission) can still be told apart. `seatFrom` can be an empty string, so test truthiness.
      const seatRange = candidate.seatFrom
        ? candidate.seatTo && candidate.seatTo !== candidate.seatFrom
          ? `Seats ${candidate.seatFrom}-${candidate.seatTo}`
          : `Seat ${candidate.seatFrom}`
        : null;

      const label =
        [
          candidate.section,
          candidate.row ? `ROW ${candidate.row}` : null,
          seatRange,
        ]
          .filter(Boolean)
          .join(' ') || `Listing ${candidate.listingId}`;

      return { candidate, label };
    });

    const countByLabel = options.reduce<Record<string, number>>(
      (acc, { label }) => ({ ...acc, [label]: (acc[label] ?? 0) + 1 }),
      {}
    );

    return options.reduce<Record<string, string>>(
      (acc, { candidate, label }) => {
        const parts = [label];

        // When two sources render identically (e.g. general admission with no seats), general admission
        // vs not is the difference the seller is actually choosing between.
        if (countByLabel[label] > 1 && candidate.sectionTypeId != null) {
          parts.push(
            isSectionTypeGeneralAdmission(candidate.sectionTypeId)
              ? GENERAL_ADMISSION_LABEL
              : NON_GENERAL_ADMISSION_LABEL
          );
        }

        // The listing id is always unique, so it guarantees the options stay distinguishable.
        if (countByLabel[label] > 1) {
          parts.push(`#${candidate.listingId}`);
        }

        acc[candidate.listingId.toString()] = parts.join(' · ');
        return acc;
      },
      {}
    );
  }, [candidates]);

  if (resolution === MergedSectionTypeResolution.RequiresSourceSelection) {
    if (Object.keys(candidateOptions).length === 0) {
      return null;
    }

    // Fall back to the parent-provided selection so a remount still reflects it.
    const selectedCandidate = candidates?.find(
      (candidate) =>
        candidate.listingId.toString() === selectedValue ||
        (selectedValue == null &&
          candidate.sectionTypeId != null &&
          candidate.sectionTypeId === selectedSectionTypeId)
    );

    return (
      <Stack direction="column" gap="s" className={styles.promptContainer}>
        <PosSelect
          showFullWidth
          searchable
          // POS-7295: the prompt sits at the bottom of the merge modal, so prefer opening the
          // dropdown upward — otherwise it is clipped by the bottom of the screen.
          contentProps={{ side: 'top' }}
          value={selectedCandidate?.listingId.toString() ?? NO_SELECTION}
          placeholderText={ContentId.ChooseSection}
          valueOptionsContent={candidateOptions}
          onChange={(value) => {
            setSelectedValue(value);
            const candidate = candidates?.find(
              (c) => c.listingId.toString() === value
            );
            if (candidate?.sectionTypeId != null) {
              onSelect(candidate.sectionTypeId);
            }
          }}
        />
      </Stack>
    );
  }

  if (resolution === MergedSectionTypeResolution.RequiresSectionMapping) {
    if (sections.length === 0) {
      // The venue map is unavailable (e.g. the event has no venue config, or this prompt is rendered
      // somewhere without venue map data). Show a message instead of rendering nothing so the seller
      // is not left with a disabled confirm button and no explanation.
      return (
        <Stack direction="column" gap="s" className={styles.promptContainer}>
          <Content id={ContentId.MergeListings_SectionMappingUnavailable} />
        </Stack>
      );
    }

    const selectedSection = sections.find(
      (section) =>
        section.id.toString() === selectedValue ||
        (selectedValue == null &&
          selectedSectionTypeId != null &&
          section.sectionType === selectedSectionTypeId)
    );

    return (
      <Stack direction="column" gap="s" className={styles.promptContainer}>
        <SectionSelector
          showFullWidth
          // POS-7295: the prompt sits at the bottom of the merge modal, so prefer opening the
          // dropdown upward — otherwise it is clipped by the bottom of the screen.
          contentProps={{ side: 'top' }}
          sections={sections}
          value={selectedSection?.id.toString() ?? NO_SELECTION}
          placeholderText={ContentId.SearchOrEnter}
          onChange={(value) => {
            setSelectedValue(value);
            const section = sections.find((s) => s.id.toString() === value);
            if (section) {
              onSelect(section.sectionType);
            }
          }}
        />
      </Stack>
    );
  }

  return null;
};

/**
 * POS-7295: renders the prompt with the venue map context for a specific event. Bulk flows can span events
 * (and are not wrapped in a single EventMapContextProvider), so each ambiguous merge group provides its own.
 */
export const MergeSectionTypePromptWithVenueMap = ({
  event,
  ...props
}: { event?: Event | null } & ComponentProps<
  typeof MergeSectionTypePrompt
>) => {
  const prompt = <MergeSectionTypePrompt {...props} />;

  return event ? (
    <EventMapContextProvider event={event} venueMapOnly>
      {prompt}
    </EventMapContextProvider>
  ) : (
    prompt
  );
};
